// Webhook integration for sending lead submissions to Albato.
// Collects form data + UTM tags + useful tracking cookies/metadata.

const WEBHOOK_URL =
  'https://h.albato.ru/wh/38/1lfdcqi/24IMVLLUQX9wapE--Mdgz0IvoaYFjOtHiEO1KwdCeYg/'

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_referrer',
  'utm_placement',
  'utm_position',
  'utm_group',
  'utm_creative',
]

const CLICK_ID_KEYS = [
  'yclid',
  'gclid',
  'fbclid',
  'ymclid',
  'msclkid',
  'ttclid',
]

const UTM_STORAGE_KEY = '__lead_utm__'
const FIRST_LANDING_KEY = '__lead_first_landing__'

type UtmData = Record<string, string>

// Capture UTM + click IDs from URL on first visit and persist them so they
// survive internal navigation. Returns the persisted data (merged with current).
function captureAndPersistUtms(): UtmData {
  if (typeof window === 'undefined') return {}

  let stored: UtmData = {}
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY)
    if (raw) stored = JSON.parse(raw) as UtmData
  } catch {
    stored = {}
  }

  const params = new URLSearchParams(window.location.search)
  const fresh: UtmData = {}

  ;[...UTM_KEYS, ...CLICK_ID_KEYS].forEach((key) => {
    const value = params.get(key)
    if (value) fresh[key] = value
  })

  // If new UTMs arrived in URL, they override stored (new campaign attribution).
  // Otherwise, keep stored values.
  const merged: UtmData =
    Object.keys(fresh).length > 0 ? { ...stored, ...fresh } : stored

  try {
    if (Object.keys(merged).length > 0) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(merged))
    }
    if (!sessionStorage.getItem(FIRST_LANDING_KEY)) {
      sessionStorage.setItem(FIRST_LANDING_KEY, window.location.href)
    }
  } catch {
    // ignore storage errors (private mode etc.)
  }

  return merged
}

// Initialize UTM capture as soon as the module loads on the client.
if (typeof window !== 'undefined') {
  captureAndPersistUtms()
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(
    new RegExp(
      '(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)',
    ),
  )
  return match ? decodeURIComponent(match[1]) : null
}

function getAllCookies(): Record<string, string> {
  if (typeof document === 'undefined') return {}
  const cookies: Record<string, string> = {}
  document.cookie.split('; ').forEach((c) => {
    if (!c) return
    const eq = c.indexOf('=')
    if (eq === -1) return
    const k = c.slice(0, eq)
    const v = c.slice(eq + 1)
    try {
      cookies[k] = decodeURIComponent(v)
    } catch {
      cookies[k] = v
    }
  })
  return cookies
}

// Extract Yandex Metrika client ID from _ym_uid cookie if present.
function getYmClientId(): string | null {
  return getCookie('_ym_uid')
}

// Extract Google Analytics client ID from _ga cookie (format: GA1.2.<cid>.<ts>)
function getGaClientId(): string | null {
  const ga = getCookie('_ga')
  if (!ga) return null
  const parts = ga.split('.')
  if (parts.length >= 4) return `${parts[2]}.${parts[3]}`
  return ga
}

export interface LeadPayload {
  name: string
  phone: string
  variant: string
  variantTitle: string
}

export async function sendLeadToWebhook(lead: LeadPayload): Promise<void> {
  if (typeof window === 'undefined') return

  const utms = captureAndPersistUtms()
  const allCookies = getAllCookies()

  // Keep only useful tracking cookies (avoid PII / session cookies).
  const trackingCookieKeys = Object.keys(allCookies).filter(
    (k) =>
      k.startsWith('_ym') ||
      k.startsWith('_ga') ||
      k.startsWith('_gid') ||
      k.startsWith('_gcl') ||
      k.startsWith('_fbp') ||
      k.startsWith('_fbc') ||
      k.startsWith('roistat') ||
      k.startsWith('calltouch') ||
      k.startsWith('_ct') ||
      k === '_ym_uid' ||
      k === '_ym_d',
  )
  const trackingCookies: Record<string, string> = {}
  trackingCookieKeys.forEach((k) => {
    trackingCookies[k] = allCookies[k]
  })

  let firstLanding: string | null = null
  try {
    firstLanding = sessionStorage.getItem(FIRST_LANDING_KEY)
  } catch {
    firstLanding = null
  }

  const payload = {
    // Lead fields
    name: lead.name,
    phone: lead.phone,
    form_variant: lead.variant,
    form_title: lead.variantTitle,
    submitted_at: new Date().toISOString(),

    // UTM + click IDs (flat for easy mapping in Albato)
    ...utms,

    // Tracking identifiers
    ym_client_id: getYmClientId(),
    ga_client_id: getGaClientId(),
    roistat_visit: getCookie('roistat_visit'),

    // Page context
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    referrer: document.referrer || null,
    first_landing_url: firstLanding,

    // Device / environment
    user_agent: navigator.userAgent,
    language: navigator.language,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Raw cookies bag (only tracking-relevant)
    cookies: trackingCookies,
  }

  try {
    const res = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      mode: 'cors',
      keepalive: true,
    })
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.warn('[webhook] non-ok response', res.status)
    }
  } catch (err) {
    // CORS or network failure — retry in no-cors so the request still fires.
    try {
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'no-cors',
        keepalive: true,
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[webhook] failed to send lead', e)
    }
  }
}
